import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useInRouterContext, useNavigate } from "react-router-dom";
import {
  IconAlertTriangle,
  IconDeviceFloppy,
  IconLoader2,
  IconLock,
  IconPlus,
  IconRotate,
  IconX,
} from "@tabler/icons-react";
import { ModalWrapper } from "@/components/ModalWrapper/ModalWrapper";
import { CapabilityCatalogueGrid } from "@/pages/event/settings/role-templates/CapabilityCatalogueGrid";
import { RowFilterRow } from "@/pages/event/settings/role-templates/RoleTemplateEditor";
import { serializeRowFilters } from "@/pages/event/settings/role-templates/roleTemplateImport";
import { FieldAllowlistDualPane } from "@/pages/event/settings/role-templates/FieldAllowlistDualPane";
import { normalizeAllowlist } from "@/lib/authz/fieldAllowlist";
import {
  exceedsCap,
  fieldScopeReducer,
  initialFieldScope,
  keepStrayCanonical,
  needsSensitiveConfirm,
  normaliseExclusions,
  type FieldScopeAction,
} from "@/lib/authz/fieldExclusion";
import { rolePreviewEditorFieldsKey, rolePreviewScopeTemplateKey } from "@/lib/authz/roleTemplateQueryKeys";
import {
  guestCatalogueStatus,
  guestFieldCatalogueQuery,
  refreshGuestFieldCatalogue,
} from "@/lib/authz/guestFieldCatalogueQuery";
import {
  noteSensitiveConfirmed,
  recheckSensitiveAdds,
  type SensitiveConfirmations,
} from "@/lib/authz/sensitiveRecheck";
import { useFieldScopeConfirm } from "@/pages/event/settings/role-templates/useFieldScopeConfirm";
import {
  roleTemplateB2CodeText,
  roleTemplateConflictOf,
  type RoleTemplateConflictCode,
} from "@/pages/event/settings/role-templates/roleTemplateErrors";
import type { EventRoleTemplate } from "@/types/EventRoleTemplate";
import {
  RoleTemplateScopePickers,
  type SlottingScopeAllowlists,
} from "@/pages/event/settings/role-templates/RoleTemplateScopePickers";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { FieldsProvider, useFields } from "@/providers/FieldsProvider";
import { InlineLoadingSpinner } from "@/components/LoadingSpinner/InlineLoadingSpinner";
import { useToast } from "@/components/Toast/Toast";
import {
  getPreviewAuth,
  hasScopeEdits,
  isRemintOwed,
  isRowGuardActive,
  saveBasisOf,
  usePreviewStore,
  type PreviewState,
} from "@/lib/preview/previewStore";
import { translateErrorCode, type ErrorsTranslator } from "@/lib/preview/previewErrors";
import { fmtCountdown } from "@/lib/preview/fmtCountdown";
import { expandImplied } from "@/lib/authz/capabilities";
import {
  CAPABILITY_CATALOGUE,
  effectiveActionBundle,
  missingActionCaps as missingActionCapsOf,
  missingFieldPrerequisites,
  type Capability,
} from "@/types/Capability";
import type { Filter } from "@/types/Filter";
import type { Field } from "@/types/Field";
import { getFields } from "@/lib/api/handlers/ViewFilterHandling";
import {
  getEventRoleTemplate,
  updateEventRoleTemplate,
} from "@/lib/api/clients/EventRoleTemplateClient";

/** Order-insensitive capability-list equality (drives the "Modified" state). */
function sameCaps(a: readonly Capability[], b: readonly Capability[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((c) => set.has(c));
}
function sameScope(
  aF: Filter[],
  bF: Filter[],
  aA: string[] | null,
  bA: string[] | null,
): boolean {
  return (
    JSON.stringify(aF ?? []) === JSON.stringify(bF ?? []) &&
    // Compare canonical shapes: the pane emits a column WITH its projection
    // token, the stored list may predate it — that token is not an edit.
    JSON.stringify(aA == null ? null : [...normalizeAllowlist(aA)!].sort()) ===
      JSON.stringify(bA == null ? null : [...normalizeAllowlist(bA)!].sort())
  );
}

/** Sorted-array equality for the three slotting id-lists (drives "Modified"). */
function sameSlottingScope(
  a: SlottingScopeAllowlists | null,
  b: SlottingScopeAllowlists | null,
): boolean {
  if (a == null || b == null) return a === b;
  const norm = (x?: string[]) => JSON.stringify([...(x ?? [])].sort());
  return (
    norm(a.visibleOfferingIds) === norm(b.visibleOfferingIds) &&
    norm(a.visibleTreatmentIds) === norm(b.visibleTreatmentIds) &&
    norm(a.visibleLaneIds) === norm(b.visibleLaneIds)
  );
}

/** The admin read of the previewed template — a full copy of THIS template, else null. */
function fullTemplateOf(d: unknown, templateId: string | undefined): EventRoleTemplate | null {
  if (!d || typeof d !== "object" || !templateId) return null;
  const t = d as Partial<EventRoleTemplate>;
  return t.eventRoleTemplateId === templateId && Array.isArray(t.capabilities) ? (d as EventRoleTemplate) : null;
}

function slottingOf(d: EventRoleTemplate): SlottingScopeAllowlists {
  return {
    visibleOfferingIds: d.visibleOfferingIds ?? [],
    visibleTreatmentIds: d.visibleTreatmentIds ?? [],
    visibleLaneIds: d.visibleLaneIds ?? [],
  };
}

const sortedJson = (xs: readonly string[] | null | undefined) => JSON.stringify(xs == null ? null : [...xs].sort());

/**
 * B2: what a newer read must equal for an editor under edits to keep going
 * (only the version moves): caps, rows, fields, mode, exclusions and the
 * slotting lists of the read the editor stands on. Without such a read (a
 * session persisted before B2) the store's base is the reference.
 */
function sameTemplateContent(
  next: EventRoleTemplate,
  prev: EventRoleTemplate | null,
  base: Pick<PreviewState, "baseCaps" | "baseRowFilters" | "baseFieldAllowlist">,
): boolean {
  const core = (caps: readonly string[], rows: unknown, fields: readonly string[] | null) =>
    JSON.stringify([sortedJson(caps), JSON.stringify(rows ?? []), sortedJson(fields)]);
  const nextCore = core(next.capabilities ?? [], next.rowFilters, next.fieldAllowlist ?? null);
  if (!prev) return nextCore === core(base.baseCaps, base.baseRowFilters, base.baseFieldAllowlist);
  const rest = (t: EventRoleTemplate) =>
    JSON.stringify([
      t.fieldAllowlistMode ?? null,
      sortedJson(t.fieldExclusions ?? []),
      sortedJson(t.visibleOfferingIds ?? []),
      sortedJson(t.visibleTreatmentIds ?? []),
      sortedJson(t.visibleLaneIds ?? []),
    ]);
  return (
    nextCore === core(prev.capabilities ?? [], prev.rowFilters, prev.fieldAllowlist ?? null) &&
    rest(next) === rest(prev)
  );
}

/** B2: why Save is off — a 409 / 400 of a save, or a newer read under edits. */
type PreviewConflict = { code: RoleTemplateConflictCode | "changedElsewhere"; rejectedIfMatch: number };

/**
 * B2: "Edit visible fields in the role editor" — an "All except" template's
 * fields are edited there, never in the bar. Stops the preview first (the role
 * editor refuses a previewed custom role), then goes there.
 *
 * B2 F1 (U-MED-2): stopping the preview drops the bar's unsaved edits, and
 * the bar has no "discard?" question — so while it holds any, the link is
 * off and says why ("save or reset first").
 */
function RoleEditorLink({ href, onClose, saveFirst }: { href: string; onClose: () => void; saveFirst: boolean }) {
  const inRouter = useInRouterContext();
  if (saveFirst) return <RoleEditorLinkButton onClick={null} />;
  return inRouter ? <RoleEditorLinkRouted href={href} onClose={onClose} /> : <RoleEditorLinkPlain href={href} onClose={onClose} />;
}

function RoleEditorLinkButton({ onClick }: { onClick: (() => void) | null }) {
  const tRole = useTranslations("roleTemplates");
  const reasonId = useId();
  const blocked = onClick === null;
  return (
    <>
      <button
        type="button"
        data-testid="role-preview-fields-locked-link"
        onClick={() => onClick?.()}
        aria-disabled={blocked || undefined}
        aria-describedby={blocked ? reasonId : undefined}
        className={`body-3-medium underline underline-offset-2 ${blocked ? "font-quaternary cursor-default" : "font-primary"}`}
      >
        {tRole("fieldScope.lockedLink")}
      </button>
      {blocked && (
        <span id={reasonId} className="block mt-1" data-testid="role-preview-fields-locked-link-reason">
          {tRole("fieldScope.lockedLinkSaveFirst")}
        </span>
      )}
    </>
  );
}

function RoleEditorLinkRouted({ href, onClose }: { href: string; onClose: () => void }) {
  const navigate = useNavigate();
  const stopPreview = usePreviewStore((s) => s.stopPreview);
  return (
    <RoleEditorLinkButton
      onClick={() => {
        onClose();
        void stopPreview().then(() => navigate(href));
      }}
    />
  );
}

function RoleEditorLinkPlain({ href, onClose }: { href: string; onClose: () => void }) {
  const stopPreview = usePreviewStore((s) => s.stopPreview);
  return (
    <RoleEditorLinkButton
      onClick={() => {
        onClose();
        void stopPreview().then(() => window.location.assign(href));
      }}
    />
  );
}

/**
 * Live role editor for the preview bar — a faithful counterpart to the template
 * editor (capabilities + row filters + field allowlist). Edits flow into the
 * preview store: capability + field-allowlist changes update the chrome
 * instantly; row-filter / field-masking changes (data scope) take effect via a
 * debounced token re-mint (surfaced as "Updating data…"). Reuses the real
 * {@link CapabilityCatalogueGrid}, {@link RowFilterRow} and
 * {@link FieldAllowlistDualPane} so it stays 1:1 with the template editor.
 *
 * Fields are fetched with the admin's own auth (bypassing the preview bearer) so
 * the picker offers the FULL catalogue, not the previewed role's restricted view.
 */
/** Isolated so the `errors` namespace hook is not called conditionally. */
function FieldFetchError() {
  const tErrors = useTranslations("errors");
  return <>{tErrors("serverError")}</>;
}

export function RolePreviewCapabilityEditor({
  canSaveToTemplate,
  onClose,
  now,
}: {
  canSaveToTemplate: boolean;
  onClose: () => void;
  /** The banner's 1 Hz clock, for the 429 pause countdown (B2-0). */
  now?: number;
}) {
  const eventId = usePreviewStore((s) => s.eventId);

  // Admin's full field catalogue (bypass the preview restriction). Not scoped
  // by the preview bearer, so a re-mint's cache bust leaves it alone — removing
  // it put the spinner in place of the editor body mid-edit (B2-0 HIGH-1).
  const fieldsQ = useQuery({
    queryKey: rolePreviewEditorFieldsKey(eventId),
    queryFn: () => getFields("guest", eventId ?? undefined, true),
    enabled: !!eventId,
    staleTime: 60_000,
    meta: { previewScopeIndependent: true },
  });
  const allFields: Field[] = Array.isArray(fieldsQ.data) ? fieldsQ.data : [];

  return (
    <ModalWrapper onClose={onClose} size="max-w-3xl" className="max-h-[88dvh]">
      <div className="surface-primary font-primary rounded-2xl border outline-quaternary flex flex-col max-h-[88dvh] overflow-hidden">
        {fieldsQ.isLoading ? (
          <div className="p-10 flex justify-center">
            <InlineLoadingSpinner />
          </div>
        ) : fieldsQ.isError ? (
          // Same reason as PRoleTemplatesModal: an empty catalogue makes every
          // stored row filter read as a deleted field. Fail visibly instead.
          <div className="p-10 text-center body-3-regular font-error">
            <FieldFetchError />
          </div>
        ) : (
          <FieldsProvider eventId={eventId ?? undefined} allFields={allFields} allowlist={null}>
            <EditorBody canSaveToTemplate={canSaveToTemplate} onClose={onClose} now={now} />
          </FieldsProvider>
        )}
      </div>
    </ModalWrapper>
  );
}

/**
 * B2 F4 (audit r4 R4-LOW-1): the preview a save was started in still runs —
 * the same session (no stop, no switch since: its `sessionEpoch`) for the
 * same template. A stop and a new start for the same role re-seed the edits,
 * so they count as gone too.
 */
function samePreviewSession(templateId: string, epoch: number): boolean {
  const s = usePreviewStore.getState();
  return (
    s.active &&
    s.target?.kind === "template" &&
    s.target.templateId === templateId &&
    getPreviewAuth().sessionEpoch === epoch
  );
}

function EditorBody({
  canSaveToTemplate,
  onClose,
  now,
}: {
  canSaveToTemplate: boolean;
  onClose: () => void;
  now?: number;
}) {
  const t = useTranslations("rolePreview");
  const tRole = useTranslations("roleTemplates");
  const tFields = useTranslations("guest-fields");
  const tRoot = useTranslations();
  const tErrors = useTranslations("errors");
  const { showToast } = useToast();
  const { allFields } = useFields();

  const eventId = usePreviewStore((s) => s.eventId);
  const target = usePreviewStore((s) => s.target);
  const baseCaps = usePreviewStore((s) => s.baseCaps);
  const effectiveCaps = usePreviewStore((s) => s.effectiveCaps);
  const setEffectiveCaps = usePreviewStore((s) => s.setEffectiveCaps);
  const baseRowFilters = usePreviewStore((s) => s.baseRowFilters);
  const effectiveRowFilters = usePreviewStore((s) => s.effectiveRowFilters);
  const setEffectiveRowFilters = usePreviewStore((s) => s.setEffectiveRowFilters);
  const baseFieldAllowlist = usePreviewStore((s) => s.baseFieldAllowlist);
  const effectiveFieldAllowlist = usePreviewStore((s) => s.effectiveFieldAllowlist);
  const setEffectiveFieldAllowlist = usePreviewStore((s) => s.setEffectiveFieldAllowlist);
  const resetCaps = usePreviewStore((s) => s.resetCaps);
  const markCapsSaved = usePreviewStore((s) => s.markCapsSaved);
  const reminting = usePreviewStore((s) => s.reminting);
  const remintError = usePreviewStore((s) => s.remintError);
  const retryRemint = usePreviewStore((s) => s.retryRemint);
  // B2-0 re-mint states (booleans/strings only — stable selector results).
  const remintErrorCode = usePreviewStore((s) => s.remintErrorCode);
  const remintPausedUntil = usePreviewStore((s) => s.remintPausedUntil);
  const expiresAt = usePreviewStore((s) => s.expiresAt);
  const remintOwed = usePreviewStore(isRemintOwed);
  // Audit r3 (store LOW-2): a forced re-mint owed shows the pause too.
  const remintDebt = usePreviewStore((s) => s.remintDebt);
  const rowGuard = usePreviewStore(
    (s) => s.target?.kind === "template" && isRowGuardActive(s),
  );
  const scopeNotApplied = usePreviewStore((s) => s.scopeUnsupported && hasScopeEdits(s));

  const [saving, setSaving] = useState(false);
  /**
   * The bar's window is still on screen. B2 F4 (audit r4 R4-LOW-1): closing
   * it does not drop a save whose re-check is still running — the preview and
   * the edits stay, so the save goes out while the preview still runs for the
   * same role (`samePreviewSession`). Only this component's own state updates
   * wait for it; the store updates and the toasts do not.
   */
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const isTemplate = target?.kind === "template";

  // Slotting resource-visibility scope is template-only (not part of the live
  // preview store / token re-mint), so fetch it for this template and edit it
  // locally. CRITICAL: it MUST be sent on save — the backend update is a
  // full-replacement, so omitting it (the previous behaviour) silently WIPED the
  // role's offering/treatment/lane restriction.
  const queryClient = useQueryClient();
  const reseedFromTemplate = usePreviewStore((s) => s.reseedFromTemplate);
  const patchTargetVersion = usePreviewStore((s) => s.patchTargetVersion);
  const templateId = isTemplate ? target?.templateId : undefined;
  const scopeQueryKey = rolePreviewScopeTemplateKey(eventId ?? "", templateId ?? "");
  const scopeQueryFn = useCallback(
    () => getEventRoleTemplate(eventId ?? "", templateId ?? "", undefined, true),
    [eventId, templateId],
  );
  const scopeTemplateQ = useQuery({
    queryKey: scopeQueryKey,
    queryFn: scopeQueryFn,
    enabled: !!eventId && isTemplate && !!templateId,
    staleTime: 60_000,
    // B2: the field mode and the If-Match come from this read — a cached copy
    // (up to 60 s old) never decides them: read again on every open.
    refetchOnMount: "always",
    // Rides the bypass (the admin's read): a re-mint leaves it alone; only a
    // slotting save (`markScopeEdited`) refetches it.
    meta: { previewScopeIndependent: true },
  });
  // B2: template targets stand on a read made after this editor mounted.
  const scopeRead =
    isTemplate && scopeTemplateQ.isFetchedAfterMount ? fullTemplateOf(scopeTemplateQ.data, templateId) : null;
  const scopeReady = !isTemplate || !!scopeRead;
  const scopeReadFailed = isTemplate && !scopeRead && scopeTemplateQ.isError;
  /** "All except" template: the field pane is locked here (edited in the role editor). */
  const lockedExclude = scopeRead?.fieldAllowlistMode === "exclude";
  const catalogueQ = useQuery({
    ...guestFieldCatalogueQuery(eventId ?? ""),
    enabled: !!eventId && lockedExclude,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const catalogue = catalogueQ.data ?? null;
  const catalogueStatus = guestCatalogueStatus(catalogueQ);
  const [scope, setScope] = useState<SlottingScopeAllowlists | null>(null);
  const [scopeBase, setScopeBase] = useState<SlottingScopeAllowlists | null>(null);
  useEffect(() => {
    const d = scopeTemplateQ.data;
    if (!d) return;
    const seeded = slottingOf(d);
    // Always set the baseline (drives touched-detection on save).
    setScopeBase(seeded);
    // The editable scope must not be clobbered if the admin toggled a picker
    // before this (60s-cached) query resolved. Merge: keep any list the user
    // already touched, fill the rest from the server seed — otherwise a partial
    // pre-load edit would leave the untouched lists undefined and the save's
    // `?? []` would wrongly CLEAR them.
    setScope((prev) =>
      prev == null
        ? seeded
        : {
            visibleOfferingIds: prev.visibleOfferingIds ?? seeded.visibleOfferingIds,
            visibleTreatmentIds: prev.visibleTreatmentIds ?? seeded.visibleTreatmentIds,
            visibleLaneIds: prev.visibleLaneIds ?? seeded.visibleLaneIds,
          },
    );
  }, [scopeTemplateQ.data]);

  const hasSlottingRead = useMemo(
    () => expandImplied(new Set(effectiveCaps)).has("slotting:read" as Capability),
    [effectiveCaps],
  );

  const capsValue = useMemo(() => new Set(effectiveCaps), [effectiveCaps]);
  // Same one-click "Aktion" entry the template editor gives — this panel edits
  // the very same caps and allowlist, so it must not be the surface where a
  // restricted preview silently keeps a dead or missing "Aktion" column.
  const actionBundle = useMemo(() => effectiveActionBundle(capsValue), [capsValue]);
  const missingActionCaps = useMemo(
    () => missingActionCapsOf(actionBundle, capsValue),
    [actionBundle, capsValue],
  );
  // B2: every field grant goes through the one reducer (`fieldExclusion.ts`)
  // — in an "All except" template it also leaves the exclusions, and the
  // sensitive confirm applies. Only the list reaches the store (and the PUT):
  // the backend keeps the mode and pulls the listed keys out of the exclusions.
  const fieldScopeNow = () =>
    initialFieldScope({
      fieldAllowlist: usePreviewStore.getState().effectiveFieldAllowlist,
      fieldAllowlistMode: lockedExclude ? "exclude" : null,
      fieldExclusions: scopeRead?.fieldExclusions ?? [],
    });
  const applyFieldAction = (action: FieldScopeAction) => {
    const cur = fieldScopeNow();
    const next = fieldScopeReducer(cur, action);
    if (next.list !== cur.list) setEffectiveFieldAllowlist(next.list);
  };
  const { ask: askScope, dialog: scopeDialog } = useFieldScopeConfirm({ elevated: true });
  /**
   * B2 F1 (S-MED-1): the keys the admin confirmed as sensitive in this editor
   * — the re-check before Save asks only about what nobody confirmed yet. Each
   * at the level it was asked at (F3 R3-LOW-2: at another level later it is
   * asked about again) — the catalogue the question was asked on.
   */
  const confirmedRef = useRef<SensitiveConfirmations>(new Map());
  const noteConfirmed = useCallback(
    (keys: readonly string[]) => noteSensitiveConfirmed(confirmedRef.current, keys, catalogue ?? []),
    [catalogue],
  );
  const fieldLabel = (k: string) => (tFields.has(k) ? tFields(k) : k);
  // Both store setters take a VALUE, not an updater (`previewStore.ts:123,130`).
  // The pane asked the sensitive question (if any) before it calls this.
  const grantAction = () => {
    setEffectiveCaps(expandImplied(new Set([...capsValue, ...actionBundle.caps])));
    applyFieldAction({ type: "addList", keys: [...actionBundle.fields] });
  };
  // …and the same SENTENCE, for the same reason. The offer alone is a control
  // with no explanation: it never says that the previewed role's check-in
  // button currently renders nothing, which is the whole point of saying it on
  // the surface the admin is previewing from. Reuses the template editor's keys
  // verbatim — no new copy, no new locale work.
  const fieldPrerequisites = useMemo(
    () => missingFieldPrerequisites(capsValue, effectiveFieldAllowlist),
    [capsValue, effectiveFieldAllowlist],
  );
  const capabilityLabel = (cap: Capability): string =>
    tRoot(CAPABILITY_CATALOGUE.find((d) => d.key === cap)?.i18nKey ?? cap, {
      defaultValue: cap,
    });
  // `null` is unrestricted — nothing to grant, and narrowing it to one field
  // here would be a silent restriction. Through the reducer like every other
  // writer: a composite column lands with its projection token (house rule
  // since the 2026-09-11 wave), and "All except" asks before a sensitive key.
  const grantField = (field: string) => {
    if (
      effectiveFieldAllowlist === null ||
      effectiveFieldAllowlist.includes(field)
    ) {
      return;
    }
    const run = () => applyFieldAction({ type: "addList", keys: [field] });
    const needs = lockedExclude ? needsSensitiveConfirm([field], catalogue ?? []) : [];
    if (needs.length === 0) {
      run();
      return;
    }
    void askScope("sensitive", needs.map(fieldLabel)).then((ok) => {
      if (!ok) return;
      noteConfirmed(needs);
      run();
    });
  };
  // "All except" without its catalogue cannot ask; a template whose read is
  // still out does not know its mode yet.
  const grantsHeld = !scopeReady || (lockedExclude && !catalogue);
  const modified = useMemo(
    () =>
      !sameCaps(effectiveCaps, baseCaps) ||
      !sameScope(
        effectiveRowFilters,
        baseRowFilters,
        effectiveFieldAllowlist,
        baseFieldAllowlist,
      ) ||
      !sameSlottingScope(scope, scopeBase),
    [
      effectiveCaps,
      baseCaps,
      effectiveRowFilters,
      baseRowFilters,
      effectiveFieldAllowlist,
      baseFieldAllowlist,
      scope,
      scopeBase,
    ],
  );

  // ── B2: the seed, a newer read, 409 / 400 ───────────────────────────────
  const [conflict, setConflict] = useState<PreviewConflict | null>(null);
  const [loadingLatest, setLoadingLatest] = useState(false);
  const [loadLatestError, setLoadLatestError] = useState<string | null>(null);
  /** B2 F2 (A1): a save met a 409 whose re-read showed only a version bump — said, Save stays on. */
  const [guestFieldsChanged, setGuestFieldsChanged] = useState(false);
  /** Our own save / Load latest is in flight: its read is no change elsewhere. */
  const suppressRef = useRef(false);
  /** The bar's heading — focus lands here when Load latest's button goes away (U-LOW-3). */
  const headingRef = useRef<HTMLHeadingElement>(null);
  /** The read the editor's base stands on (content reference for a newer one). */
  const adoptedReadRef = useRef<EventRoleTemplate | null>(null);
  useEffect(() => {
    if (!scopeRead || suppressRef.current) return;
    const st = usePreviewStore.getState();
    if (st.target?.kind !== "template" || st.target.templateId !== scopeRead.eventRoleTemplateId) return;
    const known = st.target.version;
    const readVersion = scopeRead.version ?? 0;
    // Not newer than the seed: the base stands; remember the read it matches.
    if (known !== undefined && readVersion <= known) {
      adoptedReadRef.current ??= scopeRead;
      return;
    }
    // Newer (or a session persisted before B2, without a version).
    if (!modified) {
      reseedFromTemplate(scopeRead);
      adoptedReadRef.current = scopeRead;
      const seeded = slottingOf(scopeRead);
      setScope(seeded);
      setScopeBase(seeded);
      setConflict(null);
      setLoadLatestError(null);
      setGuestFieldsChanged(false);
      // B2 F1: a newer version of an "All except" role may be a guest field
      // change (contract §7) — its catalogue is read again, uncached.
      if (scopeRead.fieldAllowlistMode === "exclude" && st.eventId) {
        void refreshGuestFieldCatalogue(queryClient, st.eventId).catch(() => undefined);
      }
      return;
    }
    if (sameTemplateContent(scopeRead, adoptedReadRef.current, st)) {
      patchTargetVersion(scopeRead.eventRoleTemplateId, readVersion);
      adoptedReadRef.current = scopeRead;
      return;
    }
    setConflict((c) => c ?? { code: "changedElsewhere", rejectedIfMatch: known ?? 0 });
  }, [scopeRead, modified, reseedFromTemplate, patchTargetVersion, queryClient]);

  const conflictText =
    conflict?.code === "ifMatchRequired"
      ? tRole("conflict.ifMatchRequired")
      : conflict
        ? tRole("conflict.versionMismatch")
        : null;

  /**
   * "Load latest (discards your changes)": read again, uncached. After a 409
   * (or a newer read under edits) only a version above the rejected If-Match
   * counts; after a 400 any good read does. A failed read is said, nothing
   * else changes.
   */
  const loadLatest = async () => {
    if (!conflict || !eventId || !templateId) return;
    setLoadingLatest(true);
    setLoadLatestError(null);
    suppressRef.current = true;
    try {
      // An "All except" role's catalogue is read again with it (uncached): the
      // change elsewhere may have been a guest field. In the background —
      // Load latest never waits for it (B2 F2 N5) — and its failure keeps
      // the cached pane.
      if (lockedExclude) void refreshGuestFieldCatalogue(queryClient, eventId).catch(() => undefined);
      const r = await queryClient.fetchQuery<unknown>({
        queryKey: scopeQueryKey,
        queryFn: scopeQueryFn,
        staleTime: 0,
      });
      const tmpl = fullTemplateOf(r, templateId);
      if (!tmpl) {
        setLoadLatestError(tRole("conflict.loadLatestFailed"));
        return;
      }
      if (conflict.code !== "ifMatchRequired" && (tmpl.version ?? 0) <= conflict.rejectedIfMatch) {
        setLoadLatestError(tRole("conflict.loadLatestNotNewer"));
        return;
      }
      reseedFromTemplate(tmpl);
      adoptedReadRef.current = tmpl;
      const seeded = slottingOf(tmpl);
      setScope(seeded);
      setScopeBase(seeded);
      setConflict(null);
      setGuestFieldsChanged(false);
      // U-LOW-3: the Load latest button goes away under the focus.
      headingRef.current?.focus();
    } catch {
      setLoadLatestError(tRole("conflict.loadLatestFailed"));
    } finally {
      suppressRef.current = false;
      setLoadingLatest(false);
    }
  };

  // RowFilterRow derives its own options/labels from the catalogue now — the
  // hand-rolled `allFields.map((f) => f.name)` option list here was the second
  // copy of the bare-key bug.
  const customFields = useMemo(
    () =>
      allFields
        .filter((f) => f.additionalField)
        .map((f) => ({ name: f.name, label: f.translation || f.name })),
    [allFields],
  );

  // Every write of the edited rows goes through here: the store keeps the RAW
  // rows for rendering (a row must not vanish mid-edit) AND the serialized rows
  // the save would send, and it mints the token from the latter. Without the
  // second argument the preview is evaluated against rows the save discards —
  // an empty `≠` row, which reaches Mongo as `$ne:""`, made the preview show a
  // strictly WIDER guest set than the role would ever receive.
  // `baseRowFilters` (the role's PERSISTED scope) goes in so the mint keeps an
  // already-stored empty-operand row exactly as the save now keeps it. Leaving it
  // out would re-open the divergence this second argument exists to close, just
  // from the other side: the mint would drop the row, the save would send it.
  const applyRowFilters = (next: Filter[]) =>
    setEffectiveRowFilters(
      next,
      serializeRowFilters(next, allFields, baseRowFilters),
    );

  const addRowFilter = () =>
    applyRowFilters([...effectiveRowFilters, { key: "", operator: "=", value: "" }]);
  const updateRowFilter = (idx: number, patch: Partial<Filter>) =>
    applyRowFilters(
      effectiveRowFilters.map((f, i) => (i === idx ? { ...f, ...patch } : f)),
    );
  const removeRowFilter = (idx: number) =>
    applyRowFilters(effectiveRowFilters.filter((_, i) => i !== idx));

  const showSave = isTemplate && canSaveToTemplate;
  // U-LOW-6: a list over the backend's 200 cap is refused — Save says so
  // (the pane shows the cap notice) instead of sending it.
  const overCap = exceedsCap(effectiveFieldAllowlist, lockedExclude ? "exclude" : "include");

  /**
   * B2 F1 (S-MED-1): before an "All except" save the catalogue is read again
   * UNCACHED (contract §8), and the keys this editor ADDED (list − seeded
   * list) are checked on it: a field made sensitive since the pane's read —
   * or one it never knew — is asked about now, unless it was confirmed here
   * already. A failed read refuses only a save that adds keys; a cancelled
   * question saves nothing. Same rule as the role editor's save — B2 F2 (A3)
   * included: after a confirm the catalogue is read again and a field raised
   * meanwhile is asked about too (`recheckSensitiveAdds`).
   */
  const recheckSensitive = async (list: readonly string[]): Promise<boolean> => {
    if (!eventId) return true;
    const read = () => refreshGuestFieldCatalogue(queryClient, eventId).catch(() => null);
    const first = await read();
    const base = new Set(usePreviewStore.getState().baseFieldAllowlist ?? []);
    const outcome = await recheckSensitiveAdds({
      added: list.filter((k) => !base.has(k)),
      first,
      reread: read,
      // F4 (audit r4 R4-LOW-1): a closed window cannot ask — nobody let the
      // field in, so the save is refused ("show anyway" text), never left hanging.
      ask: (keys) => (mountedRef.current ? askScope("sensitive", keys.map(fieldLabel)) : Promise.resolve(false)),
      confirmed: confirmedRef.current,
    });
    if (outcome === "ok") return true;
    showToast(
      outcome === "failed"
        ? tRole("fieldScope.recheckFailed")
        : outcome === "capped"
          ? tRole("fieldScope.recheckCapped")
          : tRole("fieldScope.recheckDeclined"),
      "error",
    );
    return false;
  };

  /**
   * B2 F2 (audit r2 A1): after a 409, read the role again. Only its version
   * moved (a guest field change bumps every "All except" role, contract §7)
   * ⇒ adopt that version (`patchTargetVersion`) and keep the edits; the
   * catalogue is read again, uncached, in the background. `false` = a real
   * change (or no newer read): the banner.
   */
  const onlyVersionMoved = async (rejected: number): Promise<boolean> => {
    if (!eventId || !templateId) return false;
    try {
      const tmpl = fullTemplateOf(
        await queryClient.fetchQuery<unknown>({ queryKey: scopeQueryKey, queryFn: scopeQueryFn, staleTime: 0 }),
        templateId,
      );
      if (!tmpl || (tmpl.version ?? 0) <= rejected) return false;
      if (!sameTemplateContent(tmpl, adoptedReadRef.current, usePreviewStore.getState())) return false;
      // Load-bearing (F3, audit r3 R3-L2-4): the newer-read effect adopts it
      // too only because TanStack notifies late; notified at once, it meets
      // this read while the save still suppresses it.
      patchTargetVersion(tmpl.eventRoleTemplateId, tmpl.version ?? 0);
      adoptedReadRef.current = tmpl;
      if (tmpl.fieldAllowlistMode === "exclude") {
        void refreshGuestFieldCatalogue(queryClient, eventId).catch(() => undefined);
      }
      return true;
    } catch {
      return false;
    }
  };

  const onSave = async () => {
    if (!eventId || !target || target.kind !== "template" || !target.templateId)
      return;
    // B2: the admin read made after this editor opened (bypassing the
    // read-only preview bearer) — name/description survive from it, its rows
    // are the stored ones, its mode decides the canonicaliser, and the seeded
    // version is the If-Match.
    const tmpl = scopeRead;
    if (!tmpl || conflict) return;
    const ifMatch = target.version ?? 0;
    // What the body is built from — `markCapsSaved` adopts the server's copy
    // into the edited state only while it is still exactly this.
    const basis = saveBasisOf(usePreviewStore.getState());
    // F4 (audit r4 R4-LOW-1): the preview session this save belongs to.
    const epoch = getPreviewAuth().sessionEpoch;
    setSaving(true);
    setGuestFieldsChanged(false);
    suppressRef.current = true;
    try {
      // P1, second host. This editor writes `rowFilters` too, and an empty list IS
      // "row-unrestricted" (`ResolvedAuthz.isRowUnrestricted`) — clearing the last
      // operand here handed the role every guest in the event, plus full AI tool
      // access, with a success toast. The guard lives in both hosts because only
      // one of them had it; `tmpl` is the ADMIN read of the stored document, so it
      // is the authoritative "what does the role have today".
      const storedRowFilters = tmpl.rowFilters ?? [];
      const wireRowFilters = serializeRowFilters(
        basis.effectiveRowFilters,
        allFields,
        storedRowFilters,
      );
      if (storedRowFilters.length > 0 && wireRowFilters.length === 0) {
        showToast(tErrors("rowFilterScopeWouldBeCleared"), "error");
        return;
      }
      const wireFieldAllowlist = lockedExclude
        ? keepStrayCanonical(basis.effectiveFieldAllowlist)
        : normalizeAllowlist(basis.effectiveFieldAllowlist);
      if (lockedExclude) {
        if (!(await recheckSensitive(wireFieldAllowlist ?? []))) return;
        // F4 (audit r4 R4-LOW-1): a closed window is no reason to drop the
        // save (the preview and the edits stay) — an ended or switched
        // preview is, and it is said.
        if (!samePreviewSession(target.templateId, epoch)) {
          showToast(tRole("fieldScope.recheckPreviewEnded"), "error");
          return;
        }
      }
      const touchedScope = scope != null && !sameSlottingScope(scope, scopeBase);
      // Backend update semantics: an OMITTED visible* key preserves the persisted
      // value, [] clears to unrestricted, [ids] restricts. So only send the three
      // when the admin actually touched the pickers (then [] clears / [ids]
      // restricts) — otherwise omit them so a caps-only save never disturbs the
      // stored slotting scope (this is what closes the original scope-wipe).
      const scopeFields = touchedScope
        ? {
            visibleOfferingIds: scope!.visibleOfferingIds ?? [],
            visibleTreatmentIds: scope!.visibleTreatmentIds ?? [],
            visibleLaneIds: scope!.visibleLaneIds ?? [],
          }
        : {
            visibleOfferingIds: undefined,
            visibleTreatmentIds: undefined,
            visibleLaneIds: undefined,
          };
      const res: unknown = await updateEventRoleTemplate(
        eventId,
        target.templateId,
        {
          name: tmpl.name,
          description: tmpl.description,
          capabilities: Array.from(expandImplied(new Set(basis.effectiveCaps))),
          rowFilters: wireRowFilters,
          // Canonical wire shape — the composite columns' projection tokens
          // ride along (`seating` → `seatInfo`), also when the admin never
          // touched the field pane and the stored list predates the token.
          // Second writer of the same template as RoleTemplateEditor.submit.
          // B2: no mode key (the backend keeps the template's mode and pulls
          // the listed keys out of its exclusions); an "All except" list keeps
          // its untouched stray tokens.
          fieldAllowlist: wireFieldAllowlist,
          ...scopeFields,
        },
        true,
        { ifMatch },
      );
      markCapsSaved({ template: res, basis });
      const saved = fullTemplateOf(res, target.templateId);
      if (saved) adoptedReadRef.current = saved;
      if (scope && mountedRef.current) setScopeBase(scope);
      // Slotting visibility was edited → force the board's WS to reconnect (re-read
      // the new scope) and refetch event-scoped REST so it applies instantly,
      // instead of requiring a page reload + preview restart.
      if (touchedScope) usePreviewStore.getState().markScopeEdited();
      showToast(t("saved"), "success");
    } catch (e) {
      // The client (not the handler) is imported here, so it THROWS and the
      // thrown Error's message is the backend errorCode. Discarding it turned
      // `invalidRowFilterKey` / `invalidFilterValue` — the two codes that name a
      // fixable row — into a bare "could not save". Show the specific string
      // when we have one, and keep the generic fallback for everything else.
      // B2: a stale seed (409 / 400) turns Save off and offers "Load latest"
      // — unless a 409 was only a version bump (A1): adopted, said, no error.
      const stale = roleTemplateConflictOf(e);
      if (stale === "versionMismatch" && (await onlyVersionMoved(ifMatch))) {
        if (mountedRef.current) setGuestFieldsChanged(true);
        // F4: behind a closed window the line has nowhere to show — a toast.
        else showToast(tRole("conflict.guestFieldsChanged"), "error");
        return;
      }
      if (stale && mountedRef.current) setConflict({ code: stale, rejectedIfMatch: ifMatch });
      const code = e instanceof Error ? e.message : "";
      showToast(
        roleTemplateB2CodeText(tRole, code) ??
          translateErrorCode(code, tErrors as unknown as ErrorsTranslator) ??
          t("saveError"),
        "error",
      );
    } finally {
      suppressRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };

  const roleEditorHref = templateId && eventId ? `/event/${eventId}/settings/access/roles/${templateId}` : null;

  return (
    <>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-5 py-4 border-b outline-quaternary">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 ref={headingRef} tabIndex={-1} className="body-1-bold truncate rounded-md focus-ring">
              {t("editRole")}
            </h3>
            {modified && (
              <span className="shrink-0 px-2 py-0.5 rounded-md body-4-medium chip-orange-bg chip-orange-fg">
                {t("modified")}
              </span>
            )}
          </div>
          <p className="mt-0.5 body-3-regular font-tertiary truncate">
            {target?.name}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("close")}
          className="shrink-0 p-1 rounded-md hover:surface-secondary transition-colors"
        >
          <IconX size={18} />
        </button>
      </div>

      {/* Read-only note */}
      <div className="px-5 pt-3">
        <div className="flex items-start gap-2 px-3 py-2 rounded-lg surface-secondary body-3-regular font-tertiary">
          <IconLock size={15} className="shrink-0 mt-0.5" />
          <span>{t("writesStayReadOnly")}</span>
        </div>
        {conflict && (
          <div
            role="alert"
            data-testid="role-preview-conflict"
            data-code={conflict.code}
            className="mt-2 flex flex-col gap-2 rounded-lg border outline-error px-3 py-2"
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <p className="flex-1 body-3-regular font-error">{conflictText}</p>
              <SecondaryButton
                text={tRole("conflict.loadLatest")}
                size="small"
                width="w-max"
                loading={loadingLatest}
                disabled={loadingLatest}
                onClick={() => void loadLatest()}
                testId="role-preview-load-latest"
              />
            </div>
            {loadLatestError && (
              <p className="body-3-regular font-error" data-testid="role-preview-load-latest-error">
                {loadLatestError}
              </p>
            )}
          </div>
        )}
        {/* B2 F3 (audit r3 R3-L2-1): the live region is mounted before its
            line, so the line is announced (empty, it takes no room). Save
            keeps the focus through the PUT (focusable while it runs). */}
        <div role="status" data-testid="role-preview-guest-fields-live">
          {guestFieldsChanged && !conflict && (
            <p
              data-testid="role-preview-guest-fields-changed"
              className="mt-2 rounded-lg surface-secondary px-3 py-2 body-3-regular font-secondary"
            >
              {tRole("conflict.guestFieldsChanged")}
            </p>
          )}
        </div>
        {scopeReadFailed && (
          <div
            role="alert"
            data-testid="role-preview-read-failed"
            className="mt-2 flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border outline-error px-3 py-2"
          >
            <p className="flex-1 body-3-regular font-error">{tRole("fieldScope.previewReadFailed")}</p>
            <SecondaryButton
              text={tRole("retry")}
              size="small"
              width="w-max"
              loading={scopeTemplateQ.isFetching}
              disabled={scopeTemplateQ.isFetching}
              onClick={() => void scopeTemplateQ.refetch()}
              testId="role-preview-read-retry"
            />
          </div>
        )}
      </div>

      {/* Sections (scroll) */}
      <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-6">
        <section className="flex flex-col gap-2">
          <h4 className="body-2-bold">{t("capabilities")}</h4>
          <CapabilityCatalogueGrid value={capsValue} onChange={setEffectiveCaps} />
        </section>

        <section className="flex flex-col gap-2">
          <h4 className="body-2-bold">{tRole("rowFilters")}</h4>
          <p className="body-3-regular font-tertiary">{tRole("rowFiltersHint")}</p>
          <div className="flex flex-col gap-2">
            {effectiveRowFilters.map((f, idx) => (
              <RowFilterRow
                key={idx}
                filter={f}
                idx={idx}
                fields={allFields}
                valuePlaceholder={tRole("rowFilterValuePlaceholder")}
                onUpdate={(patch) => updateRowFilter(idx, patch)}
                onRemove={() => removeRowFilter(idx)}
              />
            ))}
            <SecondaryButton
              size="small"
              width="w-max"
              icon={IconPlus}
              text={tRole("addRowFilter")}
              onClick={addRowFilter}
            />
          </div>
        </section>

        <section className="flex flex-col gap-2">
          <h4 className="body-2-bold">{tRole("fieldAllowlist")}</h4>
          {isTemplate && !scopeRead && !scopeReadFailed && (
            <p role="status" data-testid="role-preview-read-loading" className="body-3-regular font-tertiary">
              {tRole("fieldScope.previewReadLoading")}
            </p>
          )}
          {/* B2 F2 (N3): while a save (and its sensitive re-check) is out, no
              second question (the "Aktion" offer) can stack on the save's. */}
          <fieldset disabled={saving} className="m-0 p-0 border-0 min-w-0">
            <FieldAllowlistDualPane
              value={effectiveFieldAllowlist}
              onChange={setEffectiveFieldAllowlist}
              customFields={customFields}
              actionBundle={actionBundle}
              missingActionCaps={missingActionCaps}
              onGrantAction={grantAction}
              // B2: until the template's own read landed the mode is unknown.
              readOnly={!scopeReady}
              mode={lockedExclude ? "exclude" : undefined}
              exclusions={
                lockedExclude
                  ? normaliseExclusions(scopeRead?.fieldExclusions ?? [], effectiveFieldAllowlist ?? [])
                  : undefined
              }
              fieldsLocked={lockedExclude}
              lockedNote={
                lockedExclude ? (
                  <p className="body-3-regular font-tertiary" data-testid="role-preview-fields-locked">
                    {tRole("fieldScope.lockedNote")}{" "}
                    {roleEditorHref && <RoleEditorLink href={roleEditorHref} onClose={onClose} saveFirst={modified} />}
                  </p>
                ) : undefined
              }
              catalogue={lockedExclude ? catalogue : undefined}
              catalogueStatus={lockedExclude ? catalogueStatus : undefined}
              onRetryCatalogue={lockedExclude ? () => void catalogueQ.refetch() : undefined}
              catalogueRetrying={lockedExclude && catalogueQ.isFetching}
              onSensitiveConfirmed={noteConfirmed}
              elevatedConfirm
            />
          </fieldset>
          {fieldPrerequisites.map(({ cap, field }) => (
            <div
              key={`${cap}:${field}`}
              role="status"
              className="flex flex-col items-start gap-2 px-3 py-2 rounded-xl surface-secondary"
            >
              <span className="body-3-regular font-tertiary">
                {tRole("fieldPrerequisiteWarning", {
                  capability: capabilityLabel(cap),
                  field: tFields(field),
                })}
              </span>
              <SecondaryButton
                size="small"
                width="w-max"
                text={tRole("fieldPrerequisiteGrantCta", { field: tFields(field) })}
                disabled={grantsHeld || saving}
                onClick={() => grantField(field)}
              />
            </div>
          ))}
        </section>

        {isTemplate && (
          <section className="flex flex-col gap-2">
            <h4 className="body-2-bold">{tRole("slottingScope")}</h4>
            {hasSlottingRead ? (
              <RoleTemplateScopePickers
                eventId={eventId ?? undefined}
                scopes={scope ?? {}}
                onChange={setScope}
              />
            ) : (
              <p className="px-3 py-2 rounded-xl surface-secondary body-3-regular font-tertiary">
                {tRole("slottingScopeNoCapWarning")}
              </p>
            )}
          </section>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-3 px-5 py-4 border-t outline-quaternary">
        <div className="min-w-0 flex flex-col gap-1">
          {reminting ? (
            <span className="flex items-center gap-1.5 body-3-regular font-tertiary">
              <IconLoader2 size={15} className="animate-spin" />
              {t("updatingData")}
            </span>
          ) : remintPausedUntil != null && (remintOwed || remintDebt) ? (
            // 429: no retry — the pause end mints the last state by itself.
            // Audit r1 F2: the live region holds ONE static sentence; the
            // ticking m:ss is visual only (a polite region re-announced it
            // every second for up to 15 min). Colour: the themed amber token
            // (`font-warning` is no utility, F3).
            <span
              data-testid="role-preview-remint-paused"
              className="flex items-center gap-1.5 body-3-regular font-status-amber-dark"
            >
              <IconAlertTriangle size={15} className="shrink-0" aria-hidden />
              {expiresAt != null && remintPausedUntil >= expiresAt - 15_000 ? (
                <span role="status">{t("remintRateLimitedUntilEnd")}</span>
              ) : (
                <>
                  <span role="status" className="sr-only">
                    {t("remintRateLimitedStatus")}
                  </span>
                  <span aria-hidden="true">
                    {t("remintRateLimited", {
                      countdown: fmtCountdown(remintPausedUntil - (now ?? Date.now())),
                    })}
                  </span>
                </>
              )}
            </span>
          ) : remintErrorCode ? (
            // A refused body: no retry (it would be refused again); the next
            // change mints normally.
            <span
              role="status"
              data-testid="role-preview-remint-rejected"
              className="flex items-center gap-1.5 body-3-regular font-status-amber-dark"
            >
              <IconAlertTriangle size={15} className="shrink-0" />
              {translateErrorCode(remintErrorCode, tErrors as unknown as ErrorsTranslator) ??
                t("remintRejected")}
            </span>
          ) : remintError ? (
            <button
              type="button"
              data-testid="role-preview-remint-retry"
              onClick={() => void retryRemint()}
              className="flex items-center gap-1.5 body-3-regular font-status-amber-dark hover:underline"
            >
              <IconAlertTriangle size={15} />
              {t("remintError")}
            </button>
          ) : null}
          {scopeNotApplied && (
            <span
              role="status"
              data-testid="role-preview-scope-not-applied"
              className="body-3-regular font-tertiary"
            >
              {t("scopeNotApplied")}
            </span>
          )}
          {rowGuard && (
            <span
              role="status"
              data-testid="role-preview-rows-kept-stored"
              className="body-3-regular font-tertiary"
            >
              {t("rowsKeptStored")}
            </span>
          )}
        </div>
        <div className="shrink-0 flex items-center gap-2">
          <SecondaryButton
            text={t("reset")}
            icon={IconRotate}
            iconSize={15}
            size="small"
            width="w-max"
            disabled={!modified || saving}
            onClick={() => {
              resetCaps();
              setScope(scopeBase);
            }}
          />
          {showSave && (
            <PrimaryButton
              text={t("saveToTemplate")}
              icon={IconDeviceFloppy}
              iconSize={16}
              size="small"
              width="w-max"
              loading={saving}
              disabled={!modified || saving || !scopeReady || !!conflict || overCap}
              // B2 F2 (N4): off but focusable while the save runs — the
              // re-check's question hands the focus back here on Cancel (a
              // natively disabled button cannot take it); a click is a no-op.
              focusableWhenDisabled={saving}
              onClick={onSave}
            />
          )}
        </div>
      </div>
      {scopeDialog}
    </>
  );
}
